import { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { mockTickets, mockMessages, statusConfig, priorityConfig, categoryConfig, SupportTicketMessage } from '@/mocks/supportTicketsData';

export default function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const ticket = mockTickets.find((t) => t.id === id);
  const messages = id ? (mockMessages[id] || []) : [];

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, replying]);

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 rounded-full bg-background-100 dark:bg-background-200 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-2xl text-foreground-400" />
        </div>
        <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-2">Chamado não encontrado</p>
        <Link to="/support" className="text-sm text-primary-600 hover:text-primary-700 font-medium">Voltar para Suporte</Link>
      </div>
    );
  }

  const status = statusConfig[ticket.status];
  const priority = priorityConfig[ticket.priority];
  const category = categoryConfig[ticket.category] || categoryConfig.general;
  const isAdmin = profile?.role === 'admin';

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    // In a real app, this would call the API
    setReplyText('');
    setReplying(false);
    // Simulate adding a message
    const newMsg: SupportTicketMessage = {
      id: `m-new-${Date.now()}`,
      ticket_id: ticket.id,
      user_id: profile?.id || 'u1',
      is_admin: isAdmin,
      message: replyText,
      created_at: new Date().toISOString(),
      user_name: isAdmin ? 'Equipe WZap' : (profile?.full_name || 'Você'),
    };
    messages.push(newMsg);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Back button + header */}
      <div>
        <button
          onClick={() => navigate('/support')}
          className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 dark:hover:text-foreground-300 transition-colors mb-4 cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Voltar para Suporte
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-2 flex-wrap">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${status.bg}`}>
                <i className={`${category.icon} ${status.color}`} />
              </div>
              <h2 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{ticket.subject}</h2>
            </div>
            <div className="flex items-center gap-3 flex-wrap ml-[46px]">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${status.bg} ${status.color}`}>
                {status.label}
              </span>
              <span className={`text-[11px] font-medium ${priority.color}`}>
                Prioridade {priority.label}
              </span>
              <span className="text-[11px] text-foreground-400">
                {category.label}
              </span>
              <span className="text-[11px] text-foreground-400">
                #{ticket.id.slice(0, 8)}
              </span>
            </div>
          </div>

          {/* Status change (admin only) */}
          {isAdmin && (
            <div className="relative">
              <button
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                className="flex items-center gap-2 px-4 py-2 bg-background-100 dark:bg-background-200 hover:bg-background-200 dark:hover:bg-background-300 text-foreground-700 dark:text-foreground-300 text-sm font-medium rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line" /> Alterar Status
              </button>
              {showStatusMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] z-50 overflow-hidden animate-fadeIn">
                  {(['open', 'in_progress', 'resolved', 'closed'] as const).map((s) => {
                    const sc = statusConfig[s];
                    return (
                      <button
                        key={s}
                        onClick={() => setShowStatusMenu(false)}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 dark:text-foreground-300 hover:bg-background-50 dark:hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap ${
                          s === ticket.status ? 'bg-primary-50 dark:bg-primary-500/10' : ''
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${sc.bg} ${sc.color}`} />
                        {sc.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden">
        <div className="p-5 md:p-6 space-y-5 max-h-[500px] overflow-y-auto">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.is_admin ? '' : 'flex-row-reverse'}`}
            >
              {/* Avatar */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                msg.is_admin
                  ? 'bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-500/20 dark:to-primary-500/30'
                  : 'bg-gradient-to-br from-accent-100 to-accent-200 dark:from-accent-500/20 dark:to-accent-500/30'
              }`}>
                <span className="text-[10px] font-bold text-primary-700 dark:text-primary-300">
                  {msg.is_admin ? 'WZ' : (msg.user_name || '??').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                </span>
              </div>

              {/* Bubble */}
              <div className={`max-w-[75%] ${msg.is_admin ? '' : 'flex flex-col items-end'}`}>
                <div className={`rounded-2xl px-4 py-3 ${
                  msg.is_admin
                    ? 'bg-background-100 dark:bg-background-200 rounded-tl-sm'
                    : 'bg-primary-500 text-white rounded-tr-sm'
                }`}>
                  <p className={`text-sm leading-relaxed ${msg.is_admin ? 'text-foreground-700 dark:text-foreground-300' : 'text-white'}`}>
                    {msg.message}
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-1 px-1">
                  <span className={`text-[10px] ${msg.is_admin ? 'text-foreground-400' : 'text-foreground-400'}`}>
                    {msg.user_name}
                  </span>
                  <span className="text-[10px] text-foreground-400">{formatTime(msg.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply area */}
        <div className="border-t border-background-200 dark:border-background-300 p-4">
          {replying ? (
            <div className="space-y-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Escreva sua resposta..."
                maxLength={1000}
                className="w-full bg-background-50 dark:bg-background-200 border border-background-200 dark:border-background-300 rounded-xl p-3 text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 resize-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 outline-none min-h-[80px]"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-foreground-400">{replyText.length}/1000</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setReplying(false)}
                    className="px-4 py-2 text-sm font-medium text-foreground-600 dark:text-foreground-400 hover:bg-background-100 dark:hover:bg-background-200 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSendReply}
                    disabled={!replyText.trim()}
                    className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-background-200 disabled:text-foreground-400 text-white text-sm font-semibold rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <i className="ri-send-plane-fill" /> Enviar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setReplying(true)}
              className="w-full flex items-center justify-center gap-2 py-3 bg-background-50 dark:bg-background-200 hover:bg-background-100 dark:hover:bg-background-300 text-foreground-600 dark:text-foreground-400 text-sm font-medium rounded-xl transition-colors cursor-pointer"
            >
              <i className="ri-reply-line" /> Responder
            </button>
          )}
        </div>
      </div>
    </div>
  );
}